import { AbstractHistogram } from 'hdr-histogram-js'
import { DailyReport } from '../../../domain/history/DailyReport'
import { createDateRangeTrend } from '../../../domain/history/DateRangeTrend'
import {
    aggregatedServiceReportKey,
    calcDays,
    HistoryCriteria,
    historyTrendCriteria,
    transactionReportKey,
} from '../../../domain/history/HistoryCriteria'
import { AggregatedServiceReport, ServiceReport, TransactionReport } from '../../../domain/history/ServiceReport'
import { createTransactionTrendReport, TransactionTrendReport } from '../../../domain/history/TransactionTrendReport'
import { Trends } from '../../../domain/history/Trends'
import { Report } from '../../../domain/Report'
import { SerializedHistogram } from '../../../domain/SerializedHistogram'
import { TraceList } from '../../../domain/TraceSummary'
import { ApplicationState } from '../../ApplicationState'
import { getDailyReportState } from '../V2State'

export interface DailyReportState {
    byDay: { [key: string]: DailyReport<AbstractHistogram> }
    aggregated: { [key: string]: AggregatedServiceReport }
    transactions: { [key: string]: Report<AbstractHistogram> }
    // timeExplorer.production.web
    timeExplorer: { [key: string]: { [key: string]: Report<SerializedHistogram> } }
    loaded: boolean
}

export const getReport = (
    state: ApplicationState,
    day: string,
    environment: string,
    service: string,
): ServiceReport<AbstractHistogram> | null => {
    return getDailyReportState(state).byDay[day].environmentReports[environment].serviceReports[service] || null
}

export const getAggregatedReportFor = (state: ApplicationState, criteria: HistoryCriteria): AggregatedServiceReport | null => {
    const aggregatedKey = aggregatedServiceReportKey(criteria)
    const dailyReport = getDailyReportState(state)
    // @todo try to aggregate on demand if non existing yet (e.g. week-aggregate within already existing monthly)

    return dailyReport ? dailyReport.aggregated[aggregatedKey] : null
}

export const getTimeExplorerReportFor = (
    state: ApplicationState,
    environment: string,
    service: string,
): Report<SerializedHistogram> | null => {
    const { timeExplorer } = getDailyReportState(state)

    if (!timeExplorer[environment] || !timeExplorer[environment][service]) {
        return null
    }

    return timeExplorer[environment][service]
}

export const getHistoryTrendsFor = (state: ApplicationState, criteria: HistoryCriteria): Trends | null => {
    const reports = getAllAggregatedReportsRelatedTo(state, criteria)

    if (reports.length === 0) {
        return null
    }

    return { dateRangeTrends: reports.map(report => createDateRangeTrend(report.report, report.days, report.serviceName, report.noData)) }
}

export const getHistoryTrendsForTransaction = (
    state: ApplicationState,
    criteria: HistoryCriteria,
    transactionId: number,
): Trends | null => {
    const criteriaList = [...historyTrendCriteria(criteria), criteria]
    const reports = criteriaList
        .map(currentCriteria => getHistoryTransactionReport(state, currentCriteria, transactionId))
        .filter(report => Boolean(report)) as Array<Report<AbstractHistogram>>

    if (reports.length === 0) {
        return null
    }

    const daysFor = (index: number): string[] => calcDays(criteriaList[index].date, criteriaList[index].granularity)

    return {
        dateRangeTrends: reports.map((report, index) => createDateRangeTrend(report, daysFor(index), criteria.service, false)),
    }
}

export const getHistoryTransactionReport = (
    state: ApplicationState,
    criteria: HistoryCriteria,
    transactionId: number,
): Report<AbstractHistogram> | null => getDailyReportState(state).transactions[transactionReportKey(criteria, transactionId)] || null

export const getHistoryTransactionTrendReports = (state: ApplicationState, criteria: HistoryCriteria): TransactionTrendReport[] => {
    const reports = getAllAggregatedReportsRelatedTo(state, criteria)

    if (reports.length === 0) {
        return []
    }

    const currentTimeframeTransactions = reports[reports.length - 1].transactions
    const allTransactions: { [id: string | number]: TransactionReport<any> } = reports.reduce((carry, report) => {
        Object.entries(report.transactions).forEach(([id, transaction]) => {
            if (id in carry) {
                return
            }

            carry[id] = {
                ...transaction,
                worst_response: 0,
                average_response: 0,
                memory: 0,
                impact: 0,
                requests: 0,
                max: 0,
            }
        })

        return carry
    }, currentTimeframeTransactions)

    return Object.entries(allTransactions)
        .map(([id, transaction]) => createTransactionTrendReport(id, transaction, reports))
        .sort((a: TransactionTrendReport, b: TransactionTrendReport) => b.impact - a.impact)
}

export const getHistoryTransactionHistogram = (
    state: ApplicationState,
    criteria: HistoryCriteria,
    transactionId: number,
): AbstractHistogram | null => {
    const report = getHistoryTransactionReport(state, criteria, transactionId)

    if (!report) {
        return null
    }

    return report.histogram || null
}

const getAllAggregatedReportsRelatedTo = (state: ApplicationState, criteria: HistoryCriteria): AggregatedServiceReport[] => {
    const reports = [...historyTrendCriteria(criteria), criteria]
        .map(currentCriteria => getAggregatedReportFor(state, currentCriteria))
        .filter(report => Boolean(report)) as AggregatedServiceReport[]

    return areAllReportsForHistoryTrendsAvailable(reports) ? reports.filter(report => Boolean(report) && !report.noData) : []
}

const areAllReportsForHistoryTrendsAvailable = (reports: AggregatedServiceReport[]): boolean => reports.length >= 5

export const getArchivedTracesForTransaction = (globalState, criteria, transactionId): TraceList => {
    const traces = getAggregatedReportFor(globalState, criteria)?.archivedTraces

    if (!traces || traces.total === 0) {
        return { profiles: [], total: 0, aggregations: {} }
    }
    const profiles = traces.profiles.filter(trace => trace.operationId === transactionId)

    return { profiles, total: profiles.length, aggregations: {} }
}
